import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export interface SurveyQuestion {
  id: string;
  type: 'text' | 'textarea' | 'email' | 'radio' | 'select' | 'rating' | 'likert' | 'date' | 'number' | 'year' | 'textblock' | 'checkbox' | 'multiple-choice' | 'dropdown';
  questionText: string;
  required: boolean;
  description?: string; // Used for section/subsection markers (format: "Title|||Description")
  options?: string[]; // For radio, select
  ratingScale?: { min: number; max: number; labels?: { min: string; max: string } }; // For rating
  yearRange?: { start: number; end: number }; // For year type
  helpText?: string;
  followUp?: { // Conditional follow-up question
    triggerOption: string; // Which option triggers the follow-up (e.g., 'Yes')
    questionText: string; // The follow-up question text
    type: 'text' | 'textarea'; // Type of follow-up input
  };
}

export interface SurveySubsection {
  id: string;
  title: string;
  description?: string;
  questions: SurveyQuestion[];
}

export interface SurveySection {
  id: string;
  title: string;
  description?: string;
  subsections: SurveySubsection[];
  questions: SurveyQuestion[]; // Direct questions not in subsections
}

export interface Survey {
  title: string;
  description: string;
  unique_url_slug?: string;
  is_anonymous?: boolean;
  allow_multiple_submissions?: boolean;
  closing_message?: string;
  sections: SurveySection[];
}

interface SurveyBuilderProps {
  initialSurvey?: Survey;
  onSave: (survey: Survey) => void;
  onCancel: () => void;
}

const QUESTION_TYPES = [
  { value: 'textblock', label: '📝 Text Block (Description)' },
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'email', label: 'Email' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'year', label: 'Year' },
  { value: 'radio', label: 'Multiple Choice (Single)' },
  { value: 'select', label: 'Dropdown' },
  { value: 'rating', label: 'Rating Scale' },
  { value: 'likert', label: 'Likert Scale' },
];

const LIKERT_OPTIONS = [
  'Strongly Agree',
  'Agree',
  'Neutral',
  'Disagree',
  'Strongly Disagree'
];

const LIKERT_SATISFACTION_OPTIONS = [
  'Very Satisfied',
  'Satisfied',
  'Neutral',
  'Dissatisfied',
  'Very Dissatisfied'
];

const LIKERT_QUALITY_OPTIONS = [
  'Excellent',
  'Good',
  'Fair',
  'Poor',
  'Not Used'
];

const YES_NO_OPTIONS = ['Yes', 'No'];
const YES_NO_MAYBE_OPTIONS = ['Yes', 'No', 'Maybe'];

export default function SurveyBuilder({ initialSurvey, onSave, onCancel }: SurveyBuilderProps) {
  const [survey, setSurvey] = useState<Survey>(initialSurvey || {
    title: '',
    description: '',
    unique_url_slug: '',
    is_anonymous: true,
    allow_multiple_submissions: false,
    sections: [
      {
        id: `section-${Date.now()}`,
        title: 'Section 1',
        description: '',
        subsections: [],
        questions: []
      }
    ]
  });

  const [previewDialog, setPreviewDialog] = useState(false);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [recentSurveys, setRecentSurveys] = useState<Array<{key: string; name: string; timestamp: string}>>([]);

  // Load recent surveys from localStorage
  useEffect(() => {
    const loadRecentSurveys = () => {
      const surveys: Array<{key: string; name: string; timestamp: string}> = [];
      
      // Only look for the last template (new approach)
      const lastTemplateKey = 'survey_last_template';
      try {
        const data = localStorage.getItem(lastTemplateKey);
        if (data) {
          const survey = JSON.parse(data);
          surveys.push({
            key: lastTemplateKey,
            name: survey.title || 'Last Survey Template',
            timestamp: survey.timestamp || new Date().toISOString()
          });
        }
      } catch (err) {
        console.warn('Failed to parse last survey template:', err);
      }
      
      setRecentSurveys(surveys); // Only the last template
    };
    
    if (templateDialog) {
      loadRecentSurveys();
    }
  }, [templateDialog]);

  const updateSurveyField = (field: keyof Survey, value: any) => {
    setSurvey(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-generate slug from title if title changes and slug is empty
      if (field === 'title' && !prev.unique_url_slug) {
        const slug = (value as string)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
        updated.unique_url_slug = slug;
      }
      
      return updated;
    });
  };

  const addSection = () => {
    const newSection: SurveySection = {
      id: `section-${Date.now()}`,
      title: `Section ${survey.sections.length + 1}`,
      description: '',
      subsections: [],
      questions: []
    };
    setSurvey(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
  };

  const updateSection = (sectionId: string, field: keyof SurveySection, value: any) => {
    setSurvey(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId ? { ...section, [field]: value } : section
      )
    }));
  };

  const deleteSection = (sectionId: string) => {
    if (survey.sections.length === 1) {
      alert('Cannot delete the last section. A survey must have at least one section.');
      return;
    }
    setSurvey(prev => ({
      ...prev,
      sections: prev.sections.filter(section => section.id !== sectionId)
    }));
  };

  const duplicateSection = (sectionId: string) => {
    const sectionToDuplicate = survey.sections.find(s => s.id === sectionId);
    if (sectionToDuplicate) {
      const newSection: SurveySection = {
        ...sectionToDuplicate,
        id: `section-${Date.now()}`,
        title: `${sectionToDuplicate.title} (Copy)`,
        questions: sectionToDuplicate.questions.map(q => ({
          ...q,
          id: `question-${Date.now()}-${Math.random()}`
        }))
      };
      setSurvey(prev => ({
        ...prev,
        sections: [...prev.sections, newSection]
      }));
    }
  };

  // Subsection management functions
  const addSubsection = (sectionId: string) => {
    setSurvey(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId) {
          const newSubsection: SurveySubsection = {
            id: `subsection-${Date.now()}`,
            title: `Subsection ${section.subsections.length + 1}`,
            description: '',
            questions: []
          };
          return {
            ...section,
            subsections: [...section.subsections, newSubsection]
          };
        }
        return section;
      })
    }));
  };

  const updateSubsection = (sectionId: string, subsectionId: string, field: keyof SurveySubsection, value: any) => {
    setSurvey(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            subsections: section.subsections.map(subsection =>
              subsection.id === subsectionId ? { ...subsection, [field]: value } : subsection
            )
          };
        }
        return section;
      })
    }));
  };

  const deleteSubsection = (sectionId: string, subsectionId: string) => {
    setSurvey(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId) {
          return {
            ...section,
            subsections: section.subsections.filter(sub => sub.id !== subsectionId)
          };
        }
        return section;
      })
    }));
  };

  const addQuestion = (sectionId: string, questionType: SurveyQuestion['type'] = 'text', subsectionId?: string) => {
    const newQuestion: SurveyQuestion = {
      id: `question-${Date.now()}`,
      type: questionType,
      questionText: '',
      required: false,
      options: questionType === 'radio' || questionType === 'select' ? ['Option 1'] : undefined,
      ratingScale: questionType === 'rating' ? { min: 1, max: 5, labels: { min: 'Low', max: 'High' } } : undefined,
      yearRange: questionType === 'year' ? { start: new Date().getFullYear() - 50, end: new Date().getFullYear() + 10 } : undefined
    };

    // Set default options for likert
    if (questionType === 'likert') {
      newQuestion.options = [...LIKERT_OPTIONS];
    }

    if (subsectionId) {
      // Add to subsection
      setSurvey(prev => ({
        ...prev,
        sections: prev.sections.map(section => {
          if (section.id === sectionId) {
            return {
              ...section,
              subsections: section.subsections.map(subsection => {
                if (subsection.id === subsectionId) {
                  return {
                    ...subsection,
                    questions: [...subsection.questions, newQuestion]
                  };
                }
                return subsection;
              })
            };
          }
          return section;
        })
      }));
    } else {
      // Add to section directly
      updateSection(sectionId, 'questions', [
        ...survey.sections.find(s => s.id === sectionId)!.questions,
        newQuestion
      ]);
    }
  };

  const updateQuestion = (sectionId: string, questionId: string, field: keyof SurveyQuestion, value: any, subsectionId?: string) => {
    if (subsectionId) {
      // Update question in subsection
      updateSubsection(sectionId, subsectionId, 'questions', 
        survey.sections.find(s => s.id === sectionId)?.subsections.find(sub => sub.id === subsectionId)?.questions.map(q =>
          q.id === questionId ? { ...q, [field]: value } : q
        ) || []
      );
    } else {
      // Update question in section
      const section = survey.sections.find(s => s.id === sectionId);
      if (!section) return;

      const updatedQuestions = section.questions.map(question =>
        question.id === questionId ? { ...question, [field]: value } : question
      );

      updateSection(sectionId, 'questions', updatedQuestions);
    }
  };

  // New function to update multiple fields at once (prevents multiple re-renders)
  const updateQuestionMultiple = (sectionId: string, questionId: string, updates: Partial<SurveyQuestion>, subsectionId?: string) => {
    if (subsectionId) {
      // Update question in subsection
      updateSubsection(sectionId, subsectionId, 'questions',
        survey.sections.find(s => s.id === sectionId)?.subsections.find(sub => sub.id === subsectionId)?.questions.map(q =>
          q.id === questionId ? { ...q, ...updates } : q
        ) || []
      );
    } else {
      // Update question in section
      const section = survey.sections.find(s => s.id === sectionId);
      if (!section) return;

      const updatedQuestions = section.questions.map(question =>
        question.id === questionId ? { ...question, ...updates } : question
      );

      updateSection(sectionId, 'questions', updatedQuestions);
    }
  };

  const deleteQuestion = (sectionId: string, questionId: string, subsectionId?: string) => {
    if (subsectionId) {
      // Delete from subsection
      updateSubsection(sectionId, subsectionId, 'questions',
        survey.sections.find(s => s.id === sectionId)?.subsections.find(sub => sub.id === subsectionId)?.questions.filter(q => q.id !== questionId) || []
      );
    } else {
      // Delete from section
      const section = survey.sections.find(s => s.id === sectionId);
      if (!section) return;

      updateSection(sectionId, 'questions', section.questions.filter(q => q.id !== questionId));
    }
  };

  const duplicateQuestion = (sectionId: string, questionId: string, subsectionId?: string) => {
    const section = survey.sections.find(s => s.id === sectionId);
    if (!section) return;

    if (subsectionId) {
      // Duplicate in subsection
      const subsection = section.subsections.find(sub => sub.id === subsectionId);
      if (!subsection) return;

      const questionToDuplicate = subsection.questions.find(q => q.id === questionId);
      if (questionToDuplicate) {
        const newQuestion: SurveyQuestion = {
          ...questionToDuplicate,
          id: `question-${Date.now()}`,
          questionText: `${questionToDuplicate.questionText} (Copy)`
        };
        updateSubsection(sectionId, subsectionId, 'questions', [...subsection.questions, newQuestion]);
      }
    } else {
      // Duplicate in section
      const questionToDuplicate = section.questions.find(q => q.id === questionId);
      if (questionToDuplicate) {
        const newQuestion: SurveyQuestion = {
          ...questionToDuplicate,
          id: `question-${Date.now()}`,
          questionText: `${questionToDuplicate.questionText} (Copy)`
        };
        updateSection(sectionId, 'questions', [...section.questions, newQuestion]);
      }
    }
  };

  const addOption = (sectionId: string, questionId: string, subsectionId?: string) => {
    const section = survey.sections.find(s => s.id === sectionId);
    if (!section) return;

    const question = section.questions.find(q => q.id === questionId);
    if (!question || !question.options) return;

    const updatedOptions = [...question.options, `Option ${question.options.length + 1}`];
    updateQuestion(sectionId, questionId, 'options', updatedOptions);
  };

  const updateOption = (sectionId: string, questionId: string, optionIndex: number, value: string) => {
    const section = survey.sections.find(s => s.id === sectionId);
    if (!section) return;

    const question = section.questions.find(q => q.id === questionId);
    if (!question || !question.options) return;

    const updatedOptions = question.options.map((opt, idx) => idx === optionIndex ? value : opt);
    updateQuestion(sectionId, questionId, 'options', updatedOptions);
  };

  const deleteOption = (sectionId: string, questionId: string, optionIndex: number) => {
    const section = survey.sections.find(s => s.id === sectionId);
    if (!section) return;

    const question = section.questions.find(q => q.id === questionId);
    if (!question || !question.options || question.options.length <= 1) return;

    const updatedOptions = question.options.filter((_, idx) => idx !== optionIndex);
    updateQuestion(sectionId, questionId, 'options', updatedOptions);
  };

  const handleSave = () => {
    if (!survey.title.trim()) {
      alert('Please enter a survey title');
      return;
    }

    if (survey.sections.length === 0) {
      alert('Survey must have at least one section');
      return;
    }

    // Validate that all questions have text (including in subsections)
    for (const section of survey.sections) {
      for (const question of section.questions) {
        if (question.type !== 'textblock' && (!question.questionText || !question.questionText.trim())) {
          alert(`Please enter text for all questions in section "${section.title}"`);
          return;
        }
      }
      for (const subsection of section.subsections) {
        for (const question of subsection.questions) {
          if (question.type !== 'textblock' && (!question.questionText || !question.questionText.trim())) {
            alert(`Please enter text for all questions in subsection "${subsection.title}"`);
            return;
          }
        }
      }
    }

    // Helper function to convert frontend question types to backend format
    const convertQuestionType = (type: string): string => {
      const typeMap: Record<string, string> = {
        'radio': 'multiple-choice',
        'select': 'dropdown',
      };
      return typeMap[type] || type;
    };

    // Helper function to convert a question with type mapping and field transformations
    const convertQuestion = (question: SurveyQuestion): any => {
      // Convert options from string[] to QuestionOption[] format expected by backend
      const convertedOptions = question.options?.map((option, idx) => ({
        id: `opt-${question.id}-${idx}`,
        text: option,
        value: option
      }));

      return {
        ...question,
        type: convertQuestionType(question.type) as any,
        title: question.questionText, // Backend expects 'title' not 'questionText'
        options: convertedOptions, // Convert to QuestionOption format
        followUp: question.followUp, // Preserve conditional follow-up
        questionText: undefined // Remove frontend-only field
      };
    };

    // Convert subsections to flat questions for backend compatibility
    // Use description field with "Title|||Description" format for subsection headers
    const convertedSurvey: Survey = {
      ...survey,
      sections: survey.sections.map(section => {
        const allQuestions: SurveyQuestion[] = [];
        
        // Add section questions with type conversion
        section.questions.forEach(question => {
          allQuestions.push(convertQuestion(question));
        });
        
        // Add subsections by marking ALL questions with subsection header
        // This allows the public view to render the subsection header for all questions in the group
        section.subsections.forEach(subsection => {
          const subsectionDescription = subsection.title + (subsection.description ? `|||${subsection.description}` : '');
          subsection.questions.forEach(question => {
            // All questions in subsection get the same description for proper grouping
            allQuestions.push({
              ...convertQuestion(question),
              description: subsectionDescription
            });
          });
        });

        return {
          ...section,
          questions: allQuestions,
          subsections: [] // Remove subsections from final output
        };
      })
    };

    onSave(convertedSurvey);
  };

  const loadTemplate = (template: 'alumni' | 'custom') => {
    if (template === 'alumni') {
      // Load the Alumni Survey template from the user's request
      const alumniSurvey: Survey = {
        title: 'Alumni Survey',
        description: 'Dear Alumni,\n\nThank you for taking a few minutes to complete this survey. Your feedback is invaluable for Laurus College as we monitor outcomes, evaluate program effectiveness, support continuous improvement, and meet our accreditation requirements with DEAC. Your responses will remain confidential and aggregated for reporting purposes.',
        sections: [
          {
            id: `section-${Date.now()}-1`,
            title: 'Section A: Alumni Profile',
            subsections: [],
            questions: [
              { id: `q-${Date.now()}-1`, type: 'text', questionText: 'Name (optional):', required: false },
              { id: `q-${Date.now()}-2`, type: 'email', questionText: 'Email (optional):', required: false },
              { id: `q-${Date.now()}-3`, type: 'text', questionText: 'Year of graduation from Laurus College:', required: true },
              { id: `q-${Date.now()}-4`, type: 'text', questionText: 'Program / major completed:', required: true },
              { id: `q-${Date.now()}-5`, type: 'radio', questionText: 'Mode of study:', required: true, options: ['Fully online', 'Hybrid', 'In-residence'] },
              { id: `q-${Date.now()}-6`, type: 'radio', questionText: 'Have you pursued additional credentials since graduating?', required: true, options: ['Yes', 'No'] },
              { id: `q-${Date.now()}-7`, type: 'text', questionText: 'If yes, specify:', required: false },
              { id: `q-${Date.now()}-8`, type: 'radio', questionText: 'Current employment status:', required: true, options: ['Full-time', 'Part-time', 'Self-employed', 'Unemployed', 'Not in labor force'] },
              { id: `q-${Date.now()}-9`, type: 'radio', questionText: 'If employed, is your job related to your Laurus College program?', required: false, options: ['Directly', 'Somewhat', 'Not related'] },
              { id: `q-${Date.now()}-10`, type: 'text', questionText: 'Approximate annual salary (optional):', required: false },
              { id: `q-${Date.now()}-11`, type: 'textarea', questionText: 'If unemployed, please share reason (optional):', required: false },
            ]
          },
          {
            id: `section-${Date.now()}-2`,
            title: 'Section B: Program Experience & Learning Outcomes',
            description: 'Please rate the extent to which your program helped you achieve the following outcomes:',
            subsections: [],
            questions: [
              { id: `q-${Date.now()}-12`, type: 'likert', questionText: 'Acquired knowledge and skills relevant to my field', required: true, options: LIKERT_OPTIONS },
              { id: `q-${Date.now()}-13`, type: 'likert', questionText: 'Applied what I learned in real-world settings', required: true, options: LIKERT_OPTIONS },
              { id: `q-${Date.now()}-14`, type: 'likert', questionText: 'Developed critical thinking and analytical skills', required: true, options: LIKERT_OPTIONS },
              { id: `q-${Date.now()}-15`, type: 'likert', questionText: 'Gained ability to work independently', required: true, options: LIKERT_OPTIONS },
              { id: `q-${Date.now()}-16`, type: 'likert', questionText: 'Gained ability to collaborate/teamwork', required: true, options: LIKERT_OPTIONS },
              { id: `q-${Date.now()}-17`, type: 'likert', questionText: 'Developed professional communication skills', required: true, options: LIKERT_OPTIONS },
              { id: `q-${Date.now()}-18`, type: 'likert', questionText: 'Felt sufficiently prepared for employment or advancement', required: true, options: LIKERT_OPTIONS },
              { id: `q-${Date.now()}-19`, type: 'textarea', questionText: 'What aspects of your program were most valuable to you?', required: true },
              { id: `q-${Date.now()}-20`, type: 'textarea', questionText: 'What aspects of your program could be improved?', required: true },
              { id: `q-${Date.now()}-21`, type: 'likert', questionText: 'Academic advising', required: true, options: LIKERT_QUALITY_OPTIONS, helpText: 'Rate support services' },
              { id: `q-${Date.now()}-22`, type: 'likert', questionText: 'Career services', required: true, options: LIKERT_QUALITY_OPTIONS, helpText: 'Rate support services' },
              { id: `q-${Date.now()}-23`, type: 'likert', questionText: 'Library / online resources', required: true, options: LIKERT_QUALITY_OPTIONS, helpText: 'Rate support services' },
              { id: `q-${Date.now()}-24`, type: 'likert', questionText: 'Technical support', required: true, options: LIKERT_QUALITY_OPTIONS, helpText: 'Rate support services' },
              { id: `q-${Date.now()}-25`, type: 'likert', questionText: 'Financial aid / billing services', required: true, options: LIKERT_QUALITY_OPTIONS, helpText: 'Rate support services' },
            ]
          },
          {
            id: `section-${Date.now()}-3`,
            title: 'Section C: Employment Outcomes & Satisfaction',
            description: 'Please rate satisfaction with your current job:',
            subsections: [],
            questions: [
              { id: `q-${Date.now()}-26`, type: 'likert', questionText: 'Relevance of job to your field of study', required: true, options: LIKERT_SATISFACTION_OPTIONS },
              { id: `q-${Date.now()}-27`, type: 'likert', questionText: 'Compensation / salary level', required: true, options: LIKERT_SATISFACTION_OPTIONS },
              { id: `q-${Date.now()}-28`, type: 'likert', questionText: 'Opportunities for career advancement', required: true, options: LIKERT_SATISFACTION_OPTIONS },
              { id: `q-${Date.now()}-29`, type: 'likert', questionText: 'Work environment and professional culture', required: true, options: LIKERT_SATISFACTION_OPTIONS },
              { id: `q-${Date.now()}-30`, type: 'checkbox', questionText: 'Has your Laurus degree resulted in:', required: false, options: ['Promotion', 'Raise', 'New job', 'Additional education'] },
              { id: `q-${Date.now()}-31`, type: 'radio', questionText: 'How long did it take to obtain your first job in your field?', required: true, options: ['Less than 3 months', '3-6 months', '6-12 months', '12+ months'] },
              { id: `q-${Date.now()}-32`, type: 'radio', questionText: 'Would you recommend Laurus College to others?', required: true, options: YES_NO_MAYBE_OPTIONS },
              { id: `q-${Date.now()}-33`, type: 'textarea', questionText: 'Any additional employment-related comments:', required: false },
            ]
          },
          {
            id: `section-${Date.now()}-4`,
            title: 'Section D: Institutional Effectiveness & Continuous Improvement',
            description: 'Please rate your agreement:',
            subsections: [],
            questions: [
              { id: `q-${Date.now()}-34`, type: 'likert', questionText: 'The institution effectively communicated with me after graduation', required: true, options: LIKERT_OPTIONS },
              { id: `q-${Date.now()}-35`, type: 'likert', questionText: 'The institution is responsive to alumni needs', required: true, options: LIKERT_OPTIONS },
              { id: `q-${Date.now()}-36`, type: 'likert', questionText: 'The curriculum remains current and relevant to industry needs', required: true, options: LIKERT_OPTIONS },
              { id: `q-${Date.now()}-37`, type: 'likert', questionText: 'My online/hybrid experience was comparable in quality to in-residence', required: true, options: LIKERT_OPTIONS },
              { id: `q-${Date.now()}-38`, type: 'likert', questionText: 'The institution supports lifelong learning and alumni engagement', required: true, options: LIKERT_OPTIONS },
              { id: `q-${Date.now()}-39`, type: 'textarea', questionText: 'What one improvement would you most like to see at Laurus College?', required: true },
              { id: `q-${Date.now()}-40`, type: 'radio', questionText: 'Are you willing to be contacted for follow-up?', required: true, options: YES_NO_OPTIONS },
              { id: `q-${Date.now()}-41`, type: 'text', questionText: 'If yes, contact info:', required: false },
              { id: `q-${Date.now()}-42`, type: 'radio', questionText: 'Would you be willing to serve as an alumni mentor?', required: true, options: ['Yes', 'Maybe', 'No'] },
            ]
          },
          {
            id: `section-${Date.now()}-5`,
            title: 'Section E: Optional Demographic Data',
            subsections: [],
            questions: [
              { id: `q-${Date.now()}-43`, type: 'radio', questionText: 'Age range:', required: false, options: ['Under 25', '25-34', '35-44', '45-54', '55+'] },
              { id: `q-${Date.now()}-44`, type: 'radio', questionText: 'Military veteran status:', required: false, options: YES_NO_OPTIONS },
              { id: `q-${Date.now()}-45`, type: 'text', questionText: 'Gender (optional):', required: false },
              { id: `q-${Date.now()}-46`, type: 'text', questionText: 'Ethnicity (optional):', required: false },
            ]
          }
        ]
      };
      setSurvey(alumniSurvey);
      setTemplateDialog(false);
    }
  };

  const loadRecentSurvey = (surveyKey: string) => {
    try {
      const data = localStorage.getItem(surveyKey);
      console.log('🔍 Loading survey from key:', surveyKey);
      console.log('🔍 Raw localStorage data length:', data?.length);
      if (data) {
        const loadedSurvey = JSON.parse(data);
        console.log('🔍 Loaded survey:', loadedSurvey);
        console.log('🔍 Sections count:', loadedSurvey.sections?.length);
        if (loadedSurvey.sections && loadedSurvey.sections[0]) {
          console.log('🔍 First section questions count:', loadedSurvey.sections[0].questions?.length);
          if (loadedSurvey.sections[0].questions && loadedSurvey.sections[0].questions[0]) {
            console.log('🔍 First question:', loadedSurvey.sections[0].questions[0]);
          }
        }
        
        // Normalize the loaded survey structure
        // Convert options back to string[] format for frontend
        if (loadedSurvey.sections) {
          loadedSurvey.sections.forEach((section: any) => {
            section.questions.forEach((question: any) => {
              // Normalize options to string array
              if (question.options && Array.isArray(question.options)) {
                question.options = question.options.map((opt: any) => 
                  typeof opt === 'string' ? opt : ((opt as any)?.text || String(opt))
                );
              }
            });
            
            // Also normalize subsection questions
            if (section.subsections) {
              section.subsections.forEach((subsection: any) => {
                subsection.questions.forEach((question: any) => {
                  if (question.options && Array.isArray(question.options)) {
                    question.options = question.options.map((opt: any) => 
                      typeof opt === 'string' ? opt : ((opt as any)?.text || String(opt))
                    );
                  }
                });
              });
            }
          });
        }
        
        // Add timestamp if not present
        if (!loadedSurvey.timestamp) {
          loadedSurvey.timestamp = new Date().toISOString();
        }
        
        // Check if template has any questions
        const hasQuestions = loadedSurvey.sections?.some((s: any) => 
          s.questions?.length > 0 || s.subsections?.some((sub: any) => sub.questions?.length > 0)
        );
        
        if (!hasQuestions) {
          alert('⚠️ Warning: This template has no questions. You may need to add questions manually.');
        }
        
        setSurvey(loadedSurvey);
        setTemplateDialog(false);
      }
    } catch (error) {
      console.error('Failed to load recent survey:', error);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 4, mb: 3 }}>
        <Stack spacing={3}>
          <Typography variant="h5">Survey Details</Typography>
          
          <TextField
            label="Survey Title"
            value={survey.title}
            onChange={(e) => updateSurveyField('title', e.target.value)}
            required
            fullWidth
            helperText="Give your survey a clear, descriptive title"
          />

          <TextField
            label="Survey Description"
            value={survey.description}
            onChange={(e) => updateSurveyField('description', e.target.value)}
            multiline
            rows={4}
            fullWidth
            helperText="Introduce your survey and provide any necessary context"
          />

          <TextField
            label="Closing Message (Optional)"
            value={survey.closing_message || ''}
            onChange={(e) => updateSurveyField('closing_message', e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder="Thank you for taking the time to complete this survey. Your feedback is valuable to us!"
            helperText="This message will appear above the submit button"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => setTemplateDialog(true)}
            >
              Load Template
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Sections */}
      {survey.sections.map((section, sectionIndex) => (
        <Accordion key={section.id} defaultExpanded={sectionIndex === 0} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <DragIndicatorIcon sx={{ color: 'text.secondary' }} />
              <Typography sx={{ flexGrow: 1 }}>{section.title || `Section ${sectionIndex + 1}`}</Typography>
              <Chip 
                label={`${section.questions.length + section.subsections.reduce((sum, sub) => sum + sub.questions.length, 0)} questions`} 
                size="small" 
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ overflow: 'visible', position: 'relative', zIndex: 1 }}>
            <Stack spacing={3}>
              {/* Section Details */}
              <TextField
                label="Section Title"
                value={section.title}
                onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                fullWidth
              />
              <TextField
                label="Section Description (Optional)"
                value={section.description || ''}
                onChange={(e) => updateSection(section.id, 'description', e.target.value)}
                multiline
                rows={2}
                fullWidth
              />

              {/* Questions */}
              {section.questions.map((question, questionIndex) => (
                <Paper key={question.id} sx={{ 
                  p: 3, 
                  backgroundColor: '#1e3a5f', 
                  color: '#fff',
                  '& .MuiInputLabel-root': { color: '#fff' },
                  '& .MuiInputBase-input': { color: '#fff' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                  '& .MuiTypography-root': { color: '#fff' },
                  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.7)' }
                }}>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      {question.type !== 'textblock' && (
                        <TextField
                          label={`Question ${questionIndex + 1}`}
                          value={question.questionText}
                          onChange={(e) => updateQuestion(section.id, question.id, 'questionText', e.target.value)}
                          fullWidth
                          required
                        />
                      )}
                      {question.type === 'textblock' && (
                        <Typography variant="h6" sx={{ color: '#fff', flexGrow: 1, pt: 1 }}>
                          📝 Text Block {questionIndex + 1}
                        </Typography>
                      )}
                      <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={question.type}
                          onChange={(e) => {
                            const newType = e.target.value as SurveyQuestion['type'];
                            
                            // Batch all updates into a single state change to prevent re-render issues
                            const updates: Partial<SurveyQuestion> = { type: newType };
                            
                            // Set default options for specific types
                            if (newType === 'radio' || newType === 'checkbox' || newType === 'select') {
                              updates.options = ['Option 1', 'Option 2'];
                              updates.ratingScale = undefined; // Clear rating scale if it exists
                              updates.yearRange = undefined; // Clear year range if it exists
                            } else if (newType === 'likert') {
                              updates.options = LIKERT_OPTIONS;
                              updates.ratingScale = undefined;
                              updates.yearRange = undefined;
                            } else if (newType === 'rating') {
                              updates.ratingScale = { min: 1, max: 5, labels: { min: 'Low', max: 'High' } };
                              updates.options = undefined; // Clear options if they exist
                              updates.yearRange = undefined;
                            } else if (newType === 'year') {
                              const currentYear = new Date().getFullYear();
                              updates.yearRange = { start: currentYear - 50, end: currentYear + 10 };
                              updates.options = undefined;
                              updates.ratingScale = undefined;
                            } else if (newType === 'textblock') {
                              // Text block is display-only, not required, clear all input configs
                              updates.required = false;
                              updates.options = undefined;
                              updates.ratingScale = undefined;
                              updates.yearRange = undefined;
                              updates.followUp = undefined;
                            } else {
                              // For text, textarea, email, number, date - clear all special configs
                              updates.options = undefined;
                              updates.ratingScale = undefined;
                              updates.yearRange = undefined;
                            }
                            
                            updateQuestionMultiple(section.id, question.id, updates);
                          }}
                          label="Type"
                          sx={{
                            '& .MuiSelect-select': {
                              color: '#fff'
                            },
                            '& .MuiSvgIcon-root': {
                              color: '#fff'
                            }
                          }}
                          MenuProps={{
                            disableScrollLock: true,
                            PaperProps: {
                              sx: {
                                bgcolor: 'white',
                                maxHeight: 600,
                                zIndex: 9999,
                                '& .MuiMenuItem-root': {
                                  color: 'black',
                                  minHeight: '48px',
                                  '&:hover': {
                                    backgroundColor: '#f0f0f0'
                                  },
                                  '&.Mui-selected': {
                                    backgroundColor: '#e3f2fd',
                                    '&:hover': {
                                      backgroundColor: '#bbdefb'
                                    }
                                  }
                                }
                              }
                            },
                            sx: {
                              zIndex: 9999
                            }
                          }}
                        >
                          {QUESTION_TYPES.map(type => (
                            <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* For Text Block: Show larger textarea for content */}
                    {question.type === 'textblock' && (
                      <TextField
                        label="Description Text"
                        value={question.questionText}
                        onChange={(e) => updateQuestion(section.id, question.id, 'questionText', e.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                        helperText="This text will be displayed to respondents (no answer required)"
                        placeholder="Enter additional instructions, descriptions, or information here..."
                      />
                    )}

                    {/* Help Text - hide for textblock */}
                    {question.type !== 'textblock' && (
                      <TextField
                        label="Help Text (Optional)"
                        value={question.helpText || ''}
                        onChange={(e) => updateQuestion(section.id, question.id, 'helpText', e.target.value)}
                        fullWidth
                        size="small"
                        helperText="Additional guidance for respondents"
                      />
                    )}

                    {/* Options for radio, checkbox, select */}
                    {(question.type === 'radio' || question.type === 'checkbox' || question.type === 'select' || question.type === 'likert') && question.options && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>Options:</Typography>
                        <Stack spacing={1}>
                          {question.options.map((option, optionIndex) => (
                            <Box key={optionIndex} sx={{ display: 'flex', gap: 1 }}>
                              <TextField
                                value={option}
                                onChange={(e) => updateOption(section.id, question.id, optionIndex, e.target.value)}
                                size="small"
                                fullWidth
                              />
                              {question.type !== 'likert' && (
                                <IconButton
                                  size="small"
                                  onClick={() => deleteOption(section.id, question.id, optionIndex)}
                                  disabled={question.options!.length <= 1}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Box>
                          ))}
                          {question.type !== 'likert' && (
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => addOption(section.id, question.id)}
                              sx={{ alignSelf: 'flex-start' }}
                            >
                              Add Option
                            </Button>
                          )}
                        </Stack>

                        {/* Quick option templates */}
                        {question.type !== 'likert' && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                              Quick fill:
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ mr: 1, mt: 0.5 }}
                              onClick={() => updateQuestion(section.id, question.id, 'options', YES_NO_OPTIONS)}
                            >
                              Yes/No
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ mr: 1, mt: 0.5 }}
                              onClick={() => updateQuestion(section.id, question.id, 'options', YES_NO_MAYBE_OPTIONS)}
                            >
                              Yes/No/Maybe
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ mr: 1, mt: 0.5 }}
                              onClick={() => updateQuestion(section.id, question.id, 'options', LIKERT_OPTIONS)}
                            >
                              Agreement Scale
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                              onClick={() => updateQuestion(section.id, question.id, 'options', LIKERT_QUALITY_OPTIONS)}
                            >
                              Quality Scale
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}

                    {/* Follow-up Question Configuration */}
                    {(question.type === 'radio' || question.type === 'select') && question.options && question.options.length > 0 && (
                      <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'rgba(255,255,255,0.3)', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ color: '#fff' }}>
                          Conditional Follow-up Question (Optional)
                        </Typography>
                        <Stack spacing={2}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Trigger when answer is:</InputLabel>
                            <Select
                              value={question.followUp?.triggerOption || ''}
                              onChange={(e) => {
                                if (e.target.value === '') {
                                  // Remove follow-up if empty selected
                                  const { followUp, ...questionWithoutFollowUp } = question;
                                  updateQuestionMultiple(section.id, question.id, questionWithoutFollowUp);
                                } else {
                                  updateQuestion(section.id, question.id, 'followUp', {
                                    triggerOption: e.target.value,
                                    questionText: question.followUp?.questionText || 'Please provide more details:',
                                    type: question.followUp?.type || 'text'
                                  });
                                }
                              }}
                              label="Trigger when answer is:"
                            >
                              <MenuItem value="">None (no follow-up)</MenuItem>
                              {question.options.map((opt, idx) => (
                                <MenuItem key={idx} value={opt}>{opt}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {question.followUp && (
                            <>
                              <TextField
                                label="Follow-up Question"
                                value={question.followUp.questionText}
                                onChange={(e) => updateQuestion(section.id, question.id, 'followUp', {
                                  ...question.followUp!,
                                  questionText: e.target.value
                                })}
                                size="small"
                                fullWidth
                                placeholder="e.g., Please explain why you chose yes"
                              />
                              <FormControl size="small" sx={{ width: 200 }}>
                                <InputLabel>Input Type</InputLabel>
                                <Select
                                  value={question.followUp.type}
                                  onChange={(e) => updateQuestion(section.id, question.id, 'followUp', {
                                    ...question.followUp!,
                                    type: e.target.value as 'text' | 'textarea'
                                  })}
                                  label="Input Type"
                                >
                                  <MenuItem value="text">Short Text</MenuItem>
                                  <MenuItem value="textarea">Long Text</MenuItem>
                                </Select>
                              </FormControl>
                            </>
                          )}
                        </Stack>
                      </Box>
                    )}

                    {/* Rating Scale Configuration */}
                    {question.type === 'rating' && question.ratingScale && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>Rating Scale:</Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            label="Min Value"
                            type="number"
                            value={question.ratingScale.min}
                            onChange={(e) => updateQuestion(section.id, question.id, 'ratingScale', {
                              ...question.ratingScale,
                              min: parseInt(e.target.value)
                            })}
                            size="small"
                          />
                          <TextField
                            label="Max Value"
                            type="number"
                            value={question.ratingScale.max}
                            onChange={(e) => updateQuestion(section.id, question.id, 'ratingScale', {
                              ...question.ratingScale,
                              max: parseInt(e.target.value)
                            })}
                            size="small"
                          />
                          <TextField
                            label="Min Label"
                            value={question.ratingScale.labels?.min || ''}
                            onChange={(e) => updateQuestion(section.id, question.id, 'ratingScale', {
                              ...question.ratingScale!,
                              labels: { ...(question.ratingScale!.labels || {}), min: e.target.value }
                            })}
                            size="small"
                          />
                          <TextField
                            label="Max Label"
                            value={question.ratingScale.labels?.max || ''}
                            onChange={(e) => updateQuestion(section.id, question.id, 'ratingScale', {
                              ...question.ratingScale!,
                              labels: { ...(question.ratingScale!.labels || {}), max: e.target.value }
                            })}
                            size="small"
                          />
                        </Box>
                      </Box>
                    )}

                    {/* Year Range Configuration */}
                    {question.type === 'year' && question.yearRange && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>Year Range:</Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <TextField
                            label="Start Year"
                            type="number"
                            value={question.yearRange.start}
                            onChange={(e) => updateQuestion(section.id, question.id, 'yearRange', {
                              ...question.yearRange,
                              start: parseInt(e.target.value)
                            })}
                            size="small"
                            sx={{ width: 150 }}
                          />
                          <Typography variant="body2" sx={{ color: '#fff' }}>to</Typography>
                          <TextField
                            label="End Year"
                            type="number"
                            value={question.yearRange.end}
                            onChange={(e) => updateQuestion(section.id, question.id, 'yearRange', {
                              ...question.yearRange,
                              end: parseInt(e.target.value)
                            })}
                            size="small"
                            sx={{ width: 150 }}
                          />
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', ml: 1 }}>
                            ({question.yearRange.end - question.yearRange.start + 1} years)
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Question Actions */}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        {question.type !== 'textblock' && (
                          <Chip
                            label={question.required ? 'Required' : 'Optional'}
                            color={question.required ? 'primary' : 'default'}
                            size="small"
                            onClick={() => updateQuestion(section.id, question.id, 'required', !question.required)}
                            sx={{ cursor: 'pointer' }}
                          />
                        )}
                        {question.type === 'textblock' && (
                          <Chip
                            label="Display Only"
                            color="info"
                            size="small"
                            sx={{ cursor: 'default' }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={() => duplicateQuestion(section.id, question.id)}
                        >
                          Duplicate
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => deleteQuestion(section.id, question.id)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Box>
                  </Stack>
                </Paper>
              ))}

              {/* Subsections */}
              {section.subsections.map((subsection, subsectionIndex) => (
                <Box key={subsection.id} sx={{ ml: 2, pl: 3, borderLeft: '4px solid #7C3AED' }}>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', bgcolor: 'rgba(124, 58, 237, 0.1)', p: 2, borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        📂 {subsection.title}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteSubsection(section.id, subsection.id)}
                        title="Delete Subsection"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                    <TextField
                      label="Subsection Title"
                      value={subsection.title}
                      onChange={(e) => updateSubsection(section.id, subsection.id, 'title', e.target.value)}
                      fullWidth
                      size="small"
                    />

                    <TextField
                      label="Subsection Description (Optional)"
                      value={subsection.description || ''}
                      onChange={(e) => updateSubsection(section.id, subsection.id, 'description', e.target.value)}
                      multiline
                      rows={2}
                      fullWidth
                      size="small"
                    />

                    {/* Subsection Questions - Full rendering with all question type support */}
                    {subsection.questions.map((question, questionIndex) => (
                      <Paper key={question.id} sx={{ 
                        p: 2, 
                        backgroundColor: '#2a4a6f', 
                        color: '#fff',
                        '& .MuiInputLabel-root': { color: '#fff' },
                        '& .MuiInputBase-input': { color: '#fff' },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                        '& .MuiTypography-root': { color: '#fff' },
                        '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.7)' }
                      }}>
                        <Stack spacing={2}>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            {question.type !== 'textblock' && (
                              <TextField
                                label={`Question ${questionIndex + 1}`}
                                value={question.questionText}
                                onChange={(e) => updateQuestion(section.id, question.id, 'questionText', e.target.value, subsection.id)}
                                fullWidth
                                required
                                size="small"
                              />
                            )}
                            {question.type === 'textblock' && (
                              <Typography variant="h6" sx={{ color: '#fff', flexGrow: 1, pt: 1 }}>
                                📝 Text Block {questionIndex + 1}
                              </Typography>
                            )}
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                              <InputLabel>Type</InputLabel>
                              <Select
                                value={question.type}
                                onChange={(e) => {
                                  const newType = e.target.value as SurveyQuestion['type'];
                                  const updates: Partial<SurveyQuestion> = { type: newType };
                                  
                                  if (newType === 'radio' || newType === 'checkbox' || newType === 'select') {
                                    updates.options = ['Option 1', 'Option 2'];
                                    updates.ratingScale = undefined;
                                    updates.yearRange = undefined;
                                  } else if (newType === 'likert') {
                                    updates.options = LIKERT_OPTIONS;
                                    updates.ratingScale = undefined;
                                    updates.yearRange = undefined;
                                  } else if (newType === 'rating') {
                                    updates.ratingScale = { min: 1, max: 5, labels: { min: 'Low', max: 'High' } };
                                    updates.options = undefined;
                                    updates.yearRange = undefined;
                                  } else if (newType === 'year') {
                                    const currentYear = new Date().getFullYear();
                                    updates.yearRange = { start: currentYear - 50, end: currentYear + 10 };
                                    updates.options = undefined;
                                    updates.ratingScale = undefined;
                                  } else if (newType === 'textblock') {
                                    updates.required = false;
                                    updates.options = undefined;
                                    updates.ratingScale = undefined;
                                    updates.yearRange = undefined;
                                    updates.followUp = undefined;
                                  } else {
                                    updates.options = undefined;
                                    updates.ratingScale = undefined;
                                    updates.yearRange = undefined;
                                  }
                                  
                                  updateQuestionMultiple(section.id, question.id, updates, subsection.id);
                                }}
                                label="Type"
                              >
                                {QUESTION_TYPES.map(qt => (
                                  <MenuItem key={qt.value} value={qt.value}>{qt.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => duplicateQuestion(section.id, question.id, subsection.id)}
                                sx={{ color: '#fff' }}
                                title="Duplicate"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => deleteQuestion(section.id, question.id, subsection.id)}
                                title="Delete"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          {/* Text Block Description */}
                          {question.type === 'textblock' && (
                            <TextField
                              label="Description Text"
                              value={question.questionText}
                              onChange={(e) => updateQuestion(section.id, question.id, 'questionText', e.target.value, subsection.id)}
                              fullWidth
                              multiline
                              rows={3}
                              size="small"
                              helperText="This text will be displayed to respondents"
                            />
                          )}

                          {/* Help Text */}
                          {question.type !== 'textblock' && (
                            <TextField
                              label="Help Text (Optional)"
                              value={question.helpText || ''}
                              onChange={(e) => updateQuestion(section.id, question.id, 'helpText', e.target.value, subsection.id)}
                              fullWidth
                              size="small"
                            />
                          )}

                          {/* Options for radio, select, likert */}
                          {(question.type === 'radio' || question.type === 'select' || question.type === 'likert') && question.options && (
                            <Box>
                              <Typography variant="subtitle2" gutterBottom>Options:</Typography>
                              <Stack spacing={1}>
                                {question.options.map((option, optionIndex) => (
                                  <Box key={optionIndex} sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                      value={option}
                                      onChange={(e) => {
                                        const newOptions = [...question.options!];
                                        newOptions[optionIndex] = e.target.value;
                                        updateQuestion(section.id, question.id, 'options', newOptions, subsection.id);
                                      }}
                                      size="small"
                                      fullWidth
                                    />
                                    {question.type !== 'likert' && (
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          const newOptions = question.options!.filter((_, i) => i !== optionIndex);
                                          updateQuestion(section.id, question.id, 'options', newOptions, subsection.id);
                                        }}
                                        disabled={question.options!.length <= 1}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Box>
                                ))}
                                {question.type !== 'likert' && (
                                  <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                      const newOptions = [...question.options!, `Option ${question.options!.length + 1}`];
                                      updateQuestion(section.id, question.id, 'options', newOptions, subsection.id);
                                    }}
                                  >
                                    Add Option
                                  </Button>
                                )}
                              </Stack>
                            </Box>
                          )}

                          {/* Rating Scale Configuration */}
                          {question.type === 'rating' && question.ratingScale && (
                            <Box>
                              <Typography variant="subtitle2" gutterBottom>Rating Scale:</Typography>
                              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                                <TextField
                                  label="Min"
                                  type="number"
                                  value={question.ratingScale.min}
                                  onChange={(e) => updateQuestion(section.id, question.id, 'ratingScale', {
                                    ...question.ratingScale!,
                                    min: parseInt(e.target.value)
                                  }, subsection.id)}
                                  size="small"
                                />
                                <TextField
                                  label="Max"
                                  type="number"
                                  value={question.ratingScale.max}
                                  onChange={(e) => updateQuestion(section.id, question.id, 'ratingScale', {
                                    ...question.ratingScale!,
                                    max: parseInt(e.target.value)
                                  }, subsection.id)}
                                  size="small"
                                />
                              </Box>
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                  label="Min Label"
                                  value={question.ratingScale.labels?.min || ''}
                                  onChange={(e) => updateQuestion(section.id, question.id, 'ratingScale', {
                                    ...question.ratingScale!,
                                    labels: { ...question.ratingScale!.labels, min: e.target.value, max: question.ratingScale!.labels?.max || '' }
                                  }, subsection.id)}
                                  size="small"
                                  fullWidth
                                />
                                <TextField
                                  label="Max Label"
                                  value={question.ratingScale.labels?.max || ''}
                                  onChange={(e) => updateQuestion(section.id, question.id, 'ratingScale', {
                                    ...question.ratingScale!,
                                    labels: { min: question.ratingScale!.labels?.min || '', max: e.target.value }
                                  }, subsection.id)}
                                  size="small"
                                  fullWidth
                                />
                              </Box>
                            </Box>
                          )}

                          {/* Year Range Configuration */}
                          {question.type === 'year' && question.yearRange && (
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <TextField
                                label="Start Year"
                                type="number"
                                value={question.yearRange.start}
                                onChange={(e) => updateQuestion(section.id, question.id, 'yearRange', {
                                  ...question.yearRange!,
                                  start: parseInt(e.target.value)
                                }, subsection.id)}
                                size="small"
                              />
                              <TextField
                                label="End Year"
                                type="number"
                                value={question.yearRange.end}
                                onChange={(e) => updateQuestion(section.id, question.id, 'yearRange', {
                                  ...question.yearRange!,
                                  end: parseInt(e.target.value)
                                }, subsection.id)}
                                size="small"
                              />
                            </Box>
                          )}

                          {/* Quick fill buttons for subsection questions */}
                          {question.type !== 'likert' && (question.type === 'radio' || question.type === 'select') && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                Quick fill:
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ mr: 1, mt: 0.5 }}
                                onClick={() => updateQuestion(section.id, question.id, 'options', YES_NO_OPTIONS, subsection.id)}
                              >
                                Yes/No
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ mr: 1, mt: 0.5 }}
                                onClick={() => updateQuestion(section.id, question.id, 'options', YES_NO_MAYBE_OPTIONS, subsection.id)}
                              >
                                Yes/No/Maybe
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ mr: 1, mt: 0.5 }}
                                onClick={() => updateQuestion(section.id, question.id, 'options', LIKERT_OPTIONS, subsection.id)}
                              >
                                Agreement Scale
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                sx={{ mt: 0.5 }}
                                onClick={() => updateQuestion(section.id, question.id, 'options', LIKERT_QUALITY_OPTIONS, subsection.id)}
                              >
                                Quality Scale
                              </Button>
                            </Box>
                          )}

                          {/* Follow-up Question Configuration for subsection questions */}
                          {(question.type === 'radio' || question.type === 'select') && question.options && question.options.length > 0 && (
                            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'rgba(255,255,255,0.3)', borderRadius: 1 }}>
                              <Typography variant="subtitle2" gutterBottom sx={{ color: '#fff' }}>
                                Conditional Follow-up Question (Optional)
                              </Typography>
                              <Stack spacing={2}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Trigger when answer is:</InputLabel>
                                  <Select
                                    value={question.followUp?.triggerOption || ''}
                                    onChange={(e) => {
                                      if (e.target.value === '') {
                                        // Remove follow-up if empty selected
                                        const { followUp, ...questionWithoutFollowUp } = question;
                                        updateQuestionMultiple(section.id, question.id, questionWithoutFollowUp, subsection.id);
                                      } else {
                                        updateQuestion(section.id, question.id, 'followUp', {
                                          triggerOption: e.target.value,
                                          questionText: question.followUp?.questionText || 'Please provide more details:',
                                          type: question.followUp?.type || 'text'
                                        }, subsection.id);
                                      }
                                    }}
                                    label="Trigger when answer is:"
                                  >
                                    <MenuItem value="">None (no follow-up)</MenuItem>
                                    {question.options.map((opt, idx) => (
                                      <MenuItem key={idx} value={opt}>{opt}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                {question.followUp && (
                                  <>
                                    <TextField
                                      label="Follow-up Question"
                                      value={question.followUp.questionText}
                                      onChange={(e) => updateQuestion(section.id, question.id, 'followUp', {
                                        ...question.followUp!,
                                        questionText: e.target.value
                                      }, subsection.id)}
                                      fullWidth
                                      size="small"
                                    />
                                    <FormControl size="small" fullWidth>
                                      <InputLabel>Answer Type</InputLabel>
                                      <Select
                                        value={question.followUp.type}
                                        onChange={(e) => updateQuestion(section.id, question.id, 'followUp', {
                                          ...question.followUp!,
                                          type: e.target.value as 'text' | 'textarea'
                                        }, subsection.id)}
                                        label="Answer Type"
                                      >
                                        <MenuItem value="text">Short Text</MenuItem>
                                        <MenuItem value="textarea">Long Text</MenuItem>
                                      </Select>
                                    </FormControl>
                                  </>
                                )}
                              </Stack>
                            </Box>
                          )}

                          {/* Required Checkbox */}
                          {question.type !== 'textblock' && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <input
                                type="checkbox"
                                checked={question.required}
                                onChange={(e) => updateQuestion(section.id, question.id, 'required', e.target.checked, subsection.id)}
                                style={{ marginRight: '8px' }}
                              />
                              <Typography variant="body2">Required</Typography>
                            </Box>
                          )}
                        </Stack>
                      </Paper>
                    ))}

                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => addQuestion(section.id, 'text', subsection.id)}
                      size="small"
                    >
                      Add Question to Subsection
                    </Button>
                  </Stack>
                </Box>
              ))}

              {/* Add Subsection */}
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => addSubsection(section.id)}
                  fullWidth
                  sx={{ borderStyle: 'dashed' }}
                >
                  Add Subsection
                </Button>
              </Box>

              {/* Add Question */}
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => addQuestion(section.id)}
                  fullWidth
                >
                  Add Question
                </Button>
              </Box>

              {/* Section Actions */}
              <Box sx={{ display: 'flex', gap: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => duplicateSection(section.id)}
                  size="small"
                >
                  Duplicate Section
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => deleteSection(section.id)}
                  size="small"
                  disabled={survey.sections.length === 1}
                >
                  Delete Section
                </Button>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Add Section */}
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={addSection}
        fullWidth
        sx={{ mb: 3 }}
      >
        Add New Section
      </Button>

      {/* Action Buttons */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={() => setPreviewDialog(true)}>
            Preview
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Save Survey
          </Button>
        </Box>
      </Paper>

      {/* Preview Dialog - Full Live View */}
      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ background: '#7C3AED', color: 'white' }}>Survey Preview - Live View</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', minHeight: '400px' }}>
            <Box sx={{ maxWidth: '800px', margin: '0 auto', p: 3 }}>
              {/* Survey Header */}
              <Paper sx={{ p: 4, mb: 3, borderRadius: '16px' }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#7C3AED', fontWeight: 'bold' }}>
                  {survey.title}
                </Typography>
                {survey.description && (
                  <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-line', color: 'text.secondary' }}>
                    {survey.description}
                  </Typography>
                )}
              </Paper>
              
              {/* Questions by Section */}
              {survey.sections.map((section) => (
                <Box key={section.id} sx={{ mb: 2 }}>
                  {/* Section Header */}
                  <Paper sx={{ p: 2, mb: 2, background: '#7C3AED', borderRadius: '8px' }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', m: 0 }}>
                      {section.title}
                    </Typography>
                    {section.description && (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 1, fontStyle: 'italic' }}>
                        {section.description}
                      </Typography>
                    )}
                  </Paper>
                  
                  {/* Section Questions */}
                  {section.questions.map((question, qIdx) => (
                    <Paper key={question.id} sx={{ p: 3, mb: 2, borderRadius: '12px' }}>
                      {/* Question Title */}
                      <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {qIdx + 1}. {question.questionText}
                        {question.required && <span style={{ color: 'red' }}> *</span>}
                      </Typography>
                      
                      {question.helpText && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                          {question.helpText}
                        </Typography>
                      )}
                      
                      {/* Render Question Input */}
                      {question.type === 'textblock' && (
                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                          [Text Block - No input required]
                        </Typography>
                      )}
                      {question.type === 'text' && (
                        <TextField fullWidth size="small" placeholder="Short answer text" disabled />
                      )}
                      {question.type === 'textarea' && (
                        <TextField fullWidth multiline rows={4} placeholder="Long answer text" disabled />
                      )}
                      {question.type === 'email' && (
                        <TextField fullWidth size="small" type="email" placeholder="email@example.com" disabled />
                      )}
                      {question.type === 'number' && (
                        <TextField fullWidth size="small" type="number" placeholder="Enter a number" disabled />
                      )}
                      {question.type === 'date' && (
                        <TextField fullWidth size="small" type="date" disabled />
                      )}
                      {question.type === 'year' && (
                        <TextField fullWidth size="small" type="number" placeholder="YYYY" disabled />
                      )}
                      {(question.type === 'radio' || question.type === 'multiple-choice') && (
                        <Stack spacing={1}>
                          {question.options?.map((opt, idx) => {
                            // Extract text from option (handle both string and object formats)
                            const optionText = typeof opt === 'string' ? opt : ((opt as any)?.text || String(opt));
                            return (
                              <Box 
                                key={idx} 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'flex-start',
                                  padding: '12px',
                                  borderRadius: '8px',
                                  background: 'rgba(124, 58, 237, 0.1)',
                                  border: '1px solid rgba(124, 58, 237, 0.3)',
                                  cursor: 'not-allowed'
                                }}
                              >
                                <input 
                                  type="radio" 
                                  disabled 
                                  style={{ 
                                    marginRight: '12px', 
                                    marginTop: '3px',
                                    cursor: 'not-allowed'
                                  }} 
                                />
                                <Typography variant="body2" sx={{ lineHeight: '1.5' }}>{optionText}</Typography>
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                      {(question.type === 'select' || question.type === 'dropdown') && (
                        <Select fullWidth size="small" disabled displayEmpty>
                          <MenuItem value="">-- Select an option --</MenuItem>
                          {question.options?.map((opt, idx) => {
                            const optionText = typeof opt === 'string' ? opt : ((opt as any)?.text || String(opt));
                            return (
                              <MenuItem key={idx} value={optionText}>{optionText}</MenuItem>
                            );
                          })}
                        </Select>
                      )}
                      {question.type === 'rating' && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {[1, 2, 3, 4, 5].map(rating => (
                            <Chip key={rating} label={rating} variant="outlined" />
                          ))}
                        </Box>
                      )}
                      {question.type === 'likert' && (
                        <Stack spacing={1}>
                          {LIKERT_OPTIONS.map((opt, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <input type="radio" disabled />
                              <Typography variant="body2">{opt}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      )}
                      
                      {/* Conditional Follow-up */}
                      {question.followUp && (
                        <Box sx={{ ml: 4, mt: 2, p: 2, background: '#f5f5f5', borderRadius: '8px' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            ↳ If "{question.followUp.triggerOption}" is selected:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {question.followUp.questionText}
                          </Typography>
                          {question.followUp.type === 'textarea' ? (
                            <TextField fullWidth multiline rows={2} size="small" disabled />
                          ) : (
                            <TextField fullWidth size="small" disabled />
                          )}
                        </Box>
                      )}
                    </Paper>
                  ))}

                  {/* Subsections */}
                  {section.subsections.map((subsection) => (
                    <Box key={subsection.id} sx={{ ml: 4, mb: 2 }}>
                      {/* Subsection Header */}
                      <Paper sx={{ p: 2, mb: 2, background: 'rgba(124, 58, 237, 0.8)', borderRadius: '8px' }}>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: '600', fontSize: '18px', m: 0, ml: 2 }}>
                          {subsection.title}
                        </Typography>
                        {subsection.description && (
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 1, ml: 2, fontStyle: 'italic' }}>
                            {subsection.description}
                          </Typography>
                        )}
                      </Paper>

                      {/* Subsection Questions */}
                      {subsection.questions.map((question, qIdx) => (
                        <Paper key={question.id} sx={{ p: 3, mb: 2, borderRadius: '12px' }}>
                          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {qIdx + 1}. {question.questionText}
                            {question.required && <span style={{ color: 'red' }}> *</span>}
                          </Typography>
                          
                          {question.helpText && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                              {question.helpText}
                            </Typography>
                          )}
                          
                          {/* Render Question Input */}
                          {question.type === 'textblock' && (
                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                              [Text Block - No input required]
                            </Typography>
                          )}
                          {question.type === 'text' && (
                            <TextField fullWidth size="small" placeholder="Short answer text" disabled />
                          )}
                          {question.type === 'textarea' && (
                            <TextField fullWidth multiline rows={4} placeholder="Long answer text" disabled />
                          )}
                          {question.type === 'email' && (
                            <TextField fullWidth size="small" type="email" placeholder="email@example.com" disabled />
                          )}
                          {question.type === 'number' && (
                            <TextField fullWidth size="small" type="number" placeholder="Enter a number" disabled />
                          )}
                          {question.type === 'date' && (
                            <TextField fullWidth size="small" type="date" disabled />
                          )}
                          {question.type === 'year' && (
                            <TextField fullWidth size="small" type="number" placeholder="YYYY" disabled />
                          )}
                          {(question.type === 'radio' || question.type === 'multiple-choice') && (
                            <Stack spacing={1}>
                              {question.options?.map((opt, idx) => {
                                const optionText = typeof opt === 'string' ? opt : ((opt as any)?.text || String(opt));
                                return (
                                  <Box 
                                    key={idx} 
                                    sx={{ 
                                      display: 'flex', 
                                      alignItems: 'flex-start',
                                      padding: '12px',
                                      borderRadius: '8px',
                                      background: 'rgba(124, 58, 237, 0.1)',
                                      border: '1px solid rgba(124, 58, 237, 0.3)',
                                      cursor: 'not-allowed'
                                    }}
                                  >
                                    <input 
                                      type="radio" 
                                      disabled 
                                      style={{ 
                                        marginRight: '12px', 
                                        marginTop: '3px',
                                        cursor: 'not-allowed'
                                      }} 
                                    />
                                    <Typography variant="body2" sx={{ lineHeight: '1.5' }}>{optionText}</Typography>
                                  </Box>
                                );
                              })}
                            </Stack>
                          )}
                          {(question.type === 'select' || question.type === 'dropdown') && (
                            <Select fullWidth size="small" disabled displayEmpty>
                              <MenuItem value="">-- Select an option --</MenuItem>
                              {question.options?.map((opt, idx) => {
                                const optionText = typeof opt === 'string' ? opt : ((opt as any)?.text || String(opt));
                                return (
                                  <MenuItem key={idx} value={optionText}>{optionText}</MenuItem>
                                );
                              })}
                            </Select>
                          )}
                          {question.type === 'rating' && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {Array.from({ length: (question.ratingScale?.max || 5) - (question.ratingScale?.min || 1) + 1 }, (_, i) => (question.ratingScale?.min || 1) + i).map(rating => (
                                <Chip key={rating} label={rating} variant="outlined" />
                              ))}
                            </Box>
                          )}
                          {question.type === 'likert' && (
                            <Stack spacing={1}>
                              {(question.options || LIKERT_OPTIONS).map((opt, idx) => (
                                <Box 
                                  key={idx} 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'flex-start',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    background: 'rgba(124, 58, 237, 0.1)',
                                    border: '1px solid rgba(124, 58, 237, 0.3)',
                                    cursor: 'not-allowed'
                                  }}
                                >
                                  <input 
                                    type="radio" 
                                    disabled 
                                    style={{ 
                                      marginRight: '12px', 
                                      marginTop: '3px',
                                      cursor: 'not-allowed'
                                    }} 
                                  />
                                  <Typography variant="body2" sx={{ lineHeight: '1.5' }}>{opt}</Typography>
                                </Box>
                              ))}
                            </Stack>
                          )}
                          
                          {/* Conditional Follow-up */}
                          {question.followUp && (
                            <Box sx={{ ml: 4, mt: 2, p: 2, background: '#f5f5f5', borderRadius: '8px' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                ↳ If "{question.followUp.triggerOption}" is selected:
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {question.followUp.questionText}
                              </Typography>
                              {question.followUp.type === 'textarea' ? (
                                <TextField fullWidth multiline rows={2} size="small" disabled />
                              ) : (
                                <TextField fullWidth size="small" disabled />
                              )}
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Box>
                  ))}
                </Box>
              ))}
              
              {/* Closing Message */}
              {survey.closing_message && (
                <Paper sx={{ p: 3, mb: 2, background: '#7C3AED', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)' }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line', fontWeight: 'bold', color: 'white' }}>
                    {survey.closing_message}
                  </Typography>
                </Paper>
              )}
              
              {/* Submit Button */}
              <Button variant="contained" size="large" fullWidth disabled sx={{ py: 1.5 }}>
                Submit Response
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Close Preview</Button>
        </DialogActions>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={templateDialog} onClose={() => setTemplateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Load Survey Template</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Loading a template will replace your current survey. Are you sure?
          </Alert>
          
          <Stack spacing={3}>
            {/* Recently Used Surveys */}
            {recentSurveys.length > 0 && (
              <>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold', mt: 1 }}>
                  📋 Recently Used
                </Typography>
                {recentSurveys.map((recent) => (
                  <Button
                    key={recent.key}
                    variant="outlined"
                    onClick={() => loadRecentSurvey(recent.key)}
                    fullWidth
                    sx={{ py: 2, textAlign: 'left', justifyContent: 'flex-start' }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        {recent.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Last used: {new Date(recent.timestamp).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Button>
                ))}
                <Divider />
              </>
            )}

            {/* Standard Templates */}
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 'bold' }}>
              📝 Standard Templates
            </Typography>
            <Button
              variant="outlined"
              onClick={() => loadTemplate('alumni')}
              fullWidth
              sx={{ py: 2 }}
            >
              <Box>
                <Typography variant="subtitle1">Alumni Survey Template</Typography>
                <Typography variant="caption" color="text.secondary">
                  Comprehensive alumni feedback survey with 5 sections and 46 questions
                </Typography>
              </Box>
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
